import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';

// UTILS
import { useQuery } from '../../util/hooks';
import {
  PlanApprovalCheckOutcome,
  PlanApprovalCheckMessageCode,
} from '@payright/types-bnpl-checkout-api';
import { useConfig } from './../../providers/config-provider';

// REDUX AND SLICES
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../reducers';
import { fetchMerchantData } from '../../reducers/auth-slice';
import { fetchExistingData } from '../../reducers/customer-slice';
import {
  checkoutApproval,
  fetchCheckoutAttributes,
  checkoutValidate,
  checkoutCheckApprovalOutcome,
} from '../../reducers/checkout-slice';
import { fetchCustomerPaymentMethods } from '../../reducers/payment-methods-slice';
import { AppThunkDispatch } from '../../util/store';

// WEB COMPONENTS
import { Button, Loader } from '@payright/web-components';

// OTHER COMPONENTS
import SCSpinner from '../../components/spinner';
import VerificationCode from '../../components/verification-code';
import { ButtonCancelPlan } from '../../components/buttons/ButtonCancelPlan';

// LAYOUT COMPONENTS
import LargerHeaderWithNavLayout from '../../layouts/large-header-with-nav-layout';
import { StepBody } from '../../components/step-body';

// STYLES AND STYLED COMPONENTS
import SCStep2 from './css';

// EVENT HANDLERS
import { PlanResultType } from '../../types/app';
import { AlertSliceError } from '../../components/alert-slice-error';
import { useAppStepNavigation } from '../../util/hooks/useAppStepNavigation';

type Step2Props = {};

type FormData = {
  verificationcode?: string;
  yourPurchase?: boolean;
  notUnderHardship?: boolean;
};

const Step2 = (props: Step2Props) => {
  const history = useHistory();
  const dispatch = useDispatch<AppThunkDispatch>();
  const { goToStep } = useAppStepNavigation();

  // URL QUERY PARAMS
  const query = useQuery();
  const queryParamCheckoutId = query.get('checkoutId');

  // GLOBAL STATE
  const globalAuthState = useSelector((state: RootState) => state.auth);
  const globalCheckoutState = useSelector((state: RootState) => state.checkout);
  const globalPaymentState = useSelector((state: RootState) => state.payment);
  const globalPaymentMethodsState = useSelector((state: RootState) => state.paymentMethods);

  // VARIABLES
  const config = useConfig();
  const checkout = globalCheckoutState.checkout;
  const checkoutId = globalCheckoutState.checkout?.id || queryParamCheckoutId || '';
  const applicationCompleteBy = globalCheckoutState.checkout?.applicationCompletedBy || '';
  const planId = globalCheckoutState.checkout?.planId;
  const customerId = globalCheckoutState.checkout?.customerId || globalAuthState.customerId;

  const loading = globalAuthState.loading;
  const loadingCheckout = globalCheckoutState.loading;
  const loadingPayment = globalPaymentState.loading;
  const loadingPaymentMethods = globalPaymentMethodsState.loading;

  // START VERIFICATION FORM
  const reactHookForm = useForm<FormData>({
    defaultValues: {
      verificationcode: '',
      yourPurchase: false,
      notUnderHardship: false,
    },
  });

  const { handleSubmit, errors } = reactHookForm;

  // CHECKOUT PLAN VALIDATION
  const summarySubmitHandler = async (formData: FormData) => {
    const code = formData?.verificationcode;

    if (!planId || !customerId || !checkout) {
      return;
    }

    // CCCFA flow
    if (config.cccfaEnabled) {
      // Check the validation code for Ecommerce. Not required for customer led, they'd have
      // already entered the code in the beginning of the application
      if (applicationCompleteBy === 'Ecommerce') {
        const validationCodeResult = await dispatch(checkoutValidate(planId, checkoutId, code));
        if (!validationCodeResult) {
          return; // Error message will be retrieved from Redux
        }
      }

      const res = await dispatch(checkoutCheckApprovalOutcome(checkoutId));

      // handle void
      if (!res) {
        return;
      }

      const { outcome, messageCode } = res;

      if (outcome === PlanApprovalCheckOutcome.APPROVED) {
        history.push('/payment-plan/income-and-expenses' + history.location.search);
        return;
      }

      // i.e. No wages or income
      if (
        outcome === PlanApprovalCheckOutcome.REVIEW &&
        messageCode === PlanApprovalCheckMessageCode.BSO_VALIDITY_REVIEW
      ) {
        history.push('/payment-plan/plan-result' + history.location.search, {
          planResultType: PlanResultType.BSO_NOT_VALID,
        });
        return;
      }

      // i.e. Credit file not found
      if (outcome === PlanApprovalCheckOutcome.REVIEW) {
        history.push('/payment-plan/plan-result' + history.location.search);
        return;
      }

      // i.e. Declined, or Ecommerce fell into Review
      if (
        outcome === PlanApprovalCheckOutcome.DECLINED ||
        outcome === PlanApprovalCheckOutcome.CANCELLED
      ) {
        history.push('/payment-plan/plan-result' + history.location.search);
        return;
      }

      // Error message will be retrieved from Redux and displayed if any
      return;
    } else {
      // Standard flow

      // Check the validation code for Ecommerce. Not required for customer led, they'd have
      // already entered the code in the beginning of the application
      if (applicationCompleteBy === 'Ecommerce') {
        const validationCodeResult = await dispatch(checkoutValidate(planId, checkoutId, code));
        if (validationCodeResult === false) {
          return; // Error message will be retrieved from Redux
        }
      }

      const approvalResult = await dispatch(checkoutApproval(planId, checkoutId));
      if (approvalResult) {
        goToStep(approvalResult.nextStep);
      }

      // TODO: This function contains logic for Quick Links, which will need to be fixed once we're ready to reimplement it
      // approvalResultHandler(approvalResult, checkout, paymentMethods, history, dispatch);
    }
  };

  // LOAD LATEST CHECKOUT FROM THE SERVER
  useEffect(() => {
    if (queryParamCheckoutId !== null) {
      dispatch(fetchCheckoutAttributes(queryParamCheckoutId));
    }
  }, [queryParamCheckoutId, checkoutId, dispatch]);

  // LOAD EXISTING CUSTOMER DETAILS, PAYMENT METHODS, AND MERCHANT DATA
  useEffect(() => {
    if (customerId && checkoutId) {
      dispatch(fetchExistingData(checkoutId, customerId));
      dispatch(fetchCustomerPaymentMethods(checkoutId, customerId));
      dispatch(fetchMerchantData(checkoutId));
    }
  }, [customerId, checkoutId, dispatch]);
  //TODO - useIsEcommerce
  const isEcommerce = applicationCompleteBy === 'Ecommerce';
  return (
    <LargerHeaderWithNavLayout>
      <SCStep2>
        {loading || loadingCheckout || loadingPayment || loadingPaymentMethods ? (
          <div className="loading-wrapper">
            <Loader text="" />
          </div>
        ) : (
          <StepBody>
            <FormProvider {...reactHookForm}>
              <form onSubmit={handleSubmit(summarySubmitHandler)}>
                <VerificationCode
                  isEcommerce={isEcommerce}
                  customerId={customerId}
                  errors={errors}
                />
                <Button
                  className="submit-button"
                  withShadow
                  maxWidth="100%"
                  iconPosition="right"
                  disabled={loadingCheckout}
                  icon={loadingCheckout ? <SCSpinner /> : undefined}
                >
                  Submit
                </Button>
                <ButtonCancelPlan className="cancel-button-secondary" disabled={loadingCheckout} />
                <AlertSliceError slice={globalCheckoutState} />
              </form>
            </FormProvider>
          </StepBody>
        )}
      </SCStep2>
    </LargerHeaderWithNavLayout>
  );
};

export default Step2;
